import { WizIcon, WizPopUp, WizTextInput, WizValidationError, WizValidationErrors, WizValidatorFn } from '@wizeoo/akm-component-lib';
import { BusinessException, EntityModificationPack } from '@wizeoo/akm-message-lib';
import { WIZ_COMMAND_STATE } from '@wizeoo/akm-core-lib';
import { cloneDeep } from 'lodash-es';

import { CountryVO } from '../../vo/locale/country.vo';
import { Model } from '../../model/model';
import { BusinessUnitVO } from '../../vo/core/business-unit.vo';
import { LanguageVO } from '../../vo/locale/language.vo';
import { EMEngine } from '../entities/entity-modification.engine';
import { CommitCmd } from '../../command/em/commit.cmd';
import { ViewData } from '../../model/view.data';
import { EM_ACTION } from '@wizeoo/akm-compute-lib';

/**
 * Engine de pays.
 */
export class CountryEngine {

  protected _model: Model = null;
  constructor(model: Model) {
    this._model = model;
  }
  public get model(): Model {
    return this._model as Model;
  }


  /**
   * Retourne la MEME instance de pays que dans la liste du modèle.
   * @param item : CountryVO cherché
   * @return CountryVO L'instance du pays
   */
  public getCurrentInstance(source: CountryVO): CountryVO {
    let result: CountryVO = null;
    if (source && this.model.countryMap) {
      result = this.model.countryMap.get(source.code);
    }
    return result;
  }

  /**
   * Création d'un pays
   * @return CountryVO Le pays créé
   */
  public createObjectCountry(): CountryVO {
    const country = new CountryVO(this.model);
    country.domain = ViewData.wildcardDomain;
    country.businessUnitCode = ViewData.wildcardDomain;
    country.code = null;
    country.label = null;

    return country;
  }

  /**
   * Clone de pays
   * @param country Le pays à cloner
   * @return CountryVO Un clone du pays passé en paramètre
   */
  public clone(country: CountryVO): CountryVO {
    const clone = new CountryVO(this.model, cloneDeep(country.source));
    return clone;
  }

  /**
   * Vérification que l'object country est valid (attribut code et businessUnitCode)
   * Pour être valide, un pays doit avoir
   * - un code
   * - un domaine
   * - une bu
   *
   * @param country Le pays testé
   * @return boolean true si l'objet est valide, false sinon
   */
  public isValid(country: CountryVO): boolean {
    if (!country) {
      return false;
    }

    if (country.code === undefined || country.code === null) {
      return false;
    }

    if (country.domain === undefined || country.domain === null) {
      return false;
    }

    if (country.businessUnitCode === undefined || country.businessUnitCode === null) {
      return false;
    }

    return true;
  }

  /**
   * Recherche le pays à sélectionner par défaut
   * @return CountryVO le country sauvegarder dans le contexte sinon on récupère le dernier pays édité sinon le pays par défaut de la bu
   */
  public getDefaultCountry(): CountryVO {
    let result: CountryVO = null;
    // recherche d'une entrée de contexte utilisateur (key = code country)
    // const contextCountry: UserContextVO = ContextEngine.getContext(CONTEXT_RANGE.CONTEXT_TAB, CONTEXT.SELECTED_ITEM);
    // if (contextCountry) {
    //   result = this.model.countryMap.get(contextCountry.valor);
    // }

    if (!result) { // pas trouvé, context obsolète
      if (this.model.currentEditedCountry) { // dernier pays édité éditée
        result = this.getCurrentInstance(this.model.currentEditedCountry);
      } else { // BU par defaut
        let bu: BusinessUnitVO;
        if (this.model.connectedUser && this.model.businessUnitCode) { // BU du User
          bu = this.model.businessUnitEngine.getByBusinessUnitCode(this.model.domainCode, this.model.businessUnitCode);
        } else { // root
          bu = this.model.businessUnitEngine.getRootFromList(this.model.businessUnitEngine.getByDomainCode(this.model.domainCode));
        }
        if (bu) {
          result = bu.defaultCountryVO;
        }
      }
    }

    return result;
  }

  /**
   * Le pays passé en paramètre possède-t-il la langue
   * @param country Le pays
   * @param language La langue
   * @return boolean true si le pays utilise la langue en paramètre, false sinon
   */
  public hasLanguage(country?: CountryVO, language?: LanguageVO): boolean {
    let result: boolean = false;
    if (country && language) {
      if (country.languagesVO.indexOf(language) !== -1) {
        result = true;
      }
    }
    return result;
  }

  /**
   * Le pays passé en paramètre est-il utilisé?
   * @param country Le pays
   * @return boolean Le résultat du test
   */
  public isUsedCountry(pCountry: CountryVO): boolean {
    // LMA inutile, on ne va pas permettre de supprimer des pays
    // const currentCountry: CountryVO = this.getCurrentInstance(pCountry);
    // if (currentCountry && Model.instance.businessUnitList && Model.instance.businessUnitList.length > 0) {
    //   for (let i: number = 0; i < Model.instance.businessUnitList.length; i++) {
    //     const bu: BusinessUnitVO = Model.instance.businessUnitList[i];
    //     if (bu && this.model.businessUnitEngine.hasCountry(bu, currentCountry)) {
    //       return true;
    //     }
    //   }
    // }
    return false;
  }

  /**
   * Vérification de l'unicité du code du pays
   * @param clone, le pays à contrôler
   * @param tiCode, le champ de saisie
   * @returns WizValidatorFn un objet contenant le code de l'erreur si c'est le cas, null sinon
   */
  public uniqueCodeValidator(clone: CountryVO, tiCode: WizTextInput): WizValidatorFn {
    return (): WizValidationErrors | null => {

      // Pas de gestion d'erreur si le currentChronocode est null
      if (this.clone === null || this.clone === undefined) {
        return null;
      }

      if (tiCode.wizValue !== null && tiCode.wizValue && tiCode.wizValue.length > 0 && !clone.exist) {
        // Recherche d'un autre pays avec le même code
        const otherCountry = this.model.countryMap.get(tiCode.wizValue.toUpperCase());

        // Si un pays existe deja avec le meme code on affiche le message
        if (otherCountry) {
          const temp: WizValidationError = {
            wizError: '$.country.error.code.unique.$',
            wizErrorParams: []
          };
          return [temp];
        }
      }
      return null;
    };
  }

  /**
   * create/ update d'un pays
   * @param to le clone en cours de modif
   * @param from l'objet d'origine, inaltéré
   * @param callback retour dans la vue
   */
  public merge(to: CountryVO, from: CountryVO, callback: Function): void {
    let pack: EntityModificationPack;
    if (to.exist) {
      pack = EMEngine.mergeEM(to, from);
    } else {
      pack = EMEngine.createEM(to);
    }

    if (pack && pack.entries.length > 0) { // a minima une modif, sinon, c'est le même objet (update)
      new CommitCmd(this.model).commit(pack, this.backFromCall, [to], callback).execute();
    } else {
      // modèle, le clone est une copie de la valeur d'origine
      ViewData.wizUpdateModel = false;
      callback(to, WIZ_COMMAND_STATE.SUCCESS); // retour en vue
    }
  }

  /**
   * delete d'un pays
   * @param to le clone à supprimer
   * @param callback retour dans la vue
   */
  public remove(to: CountryVO, callback: Function): void {
    const pack: EntityModificationPack = EMEngine.removeEM(to);
    new CommitCmd(this.model).commit(pack, this.backFromCall, [to], callback).execute();
  }

  /**
   * gestion unifiée des retours de service CUD
   * @param cmd la commande commit qui porte les EMs et les objets
   */
  private backFromCall(cmd: CommitCmd): void {
    // modèle OK (rollback en cas d'erreur)
    ViewData.wizUpdateModel = false;

    // erreur physique
    if (cmd.state === WIZ_COMMAND_STATE.FAULT) {
      // WizPopUp.toast('$.common.toast.service.error.$', WizIcon.TOAST_ERROR).show();
    } else {
      // le VO source
      const vo: any = cmd.sourceVO[0];
      // une seule modification à la fois, packEntry donne l'objet
      const source: any = cmd.result.entries[0].entity;
      // l'action (un seul type)
      const operation: string = cmd.result.ems[0].operation;
      // la liste d'erreurs métier
      const errors: BusinessException[] = cmd.result.errors;
      // le statut du pack
      const packStatus: string = cmd.result.status;

      if (packStatus === WIZ_COMMAND_STATE.FAULT) {
        errors.forEach((be: BusinessException) => {
          WizPopUp.toast(be.message, WizIcon.TOAST_ERROR).show();
        });

      } else {
        switch (operation) {
          case EM_ACTION.UPDATE:
            vo.source = source; // le clone
            const modelCountry: CountryVO = this.model.countryMap.get(source.code);
            if (!!modelCountry) {
              modelCountry.source = source;
            }
            break;
          case EM_ACTION.CREATE:
            vo.source = source;
            this.model.countryList.push(vo);
            this.model.countryMap.set(vo.code, vo);
            break;
          case EM_ACTION.DELETE:
            vo.source = source;
            this.model.countryMap.delete(vo.code);
            this.model.countryList.splice(this.model.countryList.indexOf(vo), 1);
            break;
        }
      }
      cmd.viewCallBack(vo, packStatus); // retour en vue
    }
  }
}
